import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AdKitKey } from "types/enums";
import type { PlacementCatalogEntry } from "../../placementCatalog";
import { PlacementScreenThumb } from "../PlacementScreenThumb";
import {
  GROUP_BY_KEY,
  PLACEMENT_GROUP_ORDER,
} from "../detail/placementsTabViewModel";

interface PlacementsStepProps {
  catalog: PlacementCatalogEntry[];
  selected: AdKitKey[];
  onToggle: (key: AdKitKey) => void;
}

function Tag({ children }: { children: string }) {
  return (
    <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-semibold text-text-muted">
      {children}
    </span>
  );
}

export function PlacementsStep({
  catalog,
  selected,
  onToggle,
}: PlacementsStepProps) {
  const groups = PLACEMENT_GROUP_ORDER.map((name) => ({
    name,
    placements: catalog.filter((p) => GROUP_BY_KEY[p.key] === name),
  })).filter((group) => group.placements.length > 0);

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <p className="text-sm text-text-muted">
          Pick every spot where you want ads. Next, you&rsquo;ll set up each one
          in turn.
        </p>
        <span className="text-sm font-semibold text-ink" aria-live="polite">
          {selected.length} selected
        </span>
      </div>

      {groups.map((group) => (
        <section key={group.name}>
          <h2 className="mt-6.5 mb-2.5 text-xs font-bold tracking-wide text-text-muted uppercase">
            {group.name}
          </h2>
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            {group.placements.map((placement) => {
              const on = selected.includes(placement.key);
              return (
                <button
                  key={placement.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onToggle(placement.key)}
                  className={cn(
                    "relative flex flex-col gap-2 rounded-2xl border-[1.5px] bg-surface p-3.5 text-left transition-colors",
                    on ? "border-primary" : "border-border hover:border-ink/40",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "absolute top-3 right-3 z-10 flex size-5 items-center justify-center rounded-md border-[1.5px]",
                      on
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-surface",
                    )}
                  >
                    {on && <Check className="size-3" />}
                  </span>
                  <PlacementScreenThumb
                    zone={placement.screenZone}
                    className="w-full"
                  />
                  <span className="poppins text-base font-semibold text-ink">
                    {placement.name}
                  </span>
                  <span className="text-xs leading-snug text-text-muted">
                    {placement.whereItShows}
                  </span>
                  <span className="text-xs text-text-muted">
                    {placement.imageSize}
                  </span>
                  <span className="flex flex-wrap gap-1">
                    <Tag>{placement.howItChanges}</Tag>
                    {placement.canAssignToSpecificHoles && (
                      <Tag>Hole sponsors</Tag>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
